import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import english from '../../data/chartCopy/en.json';
import {
  ChartLanguageProvider, EmText, chartDirection, chartHref, useChartCopy, useChartLanguage,
} from '../../data/chartLanguage';
import BirthChartTool from './BirthChartTool';
import { MORE } from './ChartLanding';
import './BirthChart.css';

const BirthChartPage = () => {
  const [drawn, setDrawn] = useState(false);
  const { language } = useChartLanguage();
  const copy = useChartCopy(language);
  const page = copy.chart;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebApplication',
        name: 'Free Birth Chart',
        url: 'https://nowtarot.com/birth-chart/',
        applicationCategory: 'LifestyleApplication',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        description: 'Draw a free natal chart from your birth date, time, and place, then read it and ask questions about it.',
      },
      {
        '@type': 'FAQPage',
        mainEntity: english.chart.faq.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };

  return (
    <div className="birth-page">
      <Helmet>
        <title>Free Birth Chart Calculator with AI Reading | NowTarot AI</title>
        <meta name="description" content="Free birth chart calculator. Enter your birth date, time, and place, get a full AI reading, then ask questions about your natal chart." />
        <link rel="canonical" href="https://nowtarot.com/birth-chart/" />
        <meta property="og:title" content="Free Birth Chart Calculator with AI Reading | NowTarot AI" />
        <meta property="og:description" content="A natal chart drawn to the degree, a full reading, and questions you can ask about it." />
        <meta property="og:url" content="https://nowtarot.com/birth-chart/" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />
      <main className={drawn ? 'birth-wide' : ''} {...chartDirection(language)}>
        <BirthChartTool onDrawnChange={setDrawn} />
        {!drawn && (
          <section className="birth-about">
            <article className="birth-box">
              <h2><EmText text={page.aboutTitle} /></h2>
              <p>{page.about}</p>
            </article>
            <div className="birth-points">
              {page.points.map((point, index) => (
                <article key={point.title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{point.title}</h3>
                  <p>{point.text}</p>
                </article>
              ))}
            </div>
            {page.askExamplesTitle && (
              <div className="birth-box birth-ask-examples">
                <h2>{page.askExamplesTitle}</h2>
                <p>{page.askExamplesText}</p>
                <ul>
                  {(page.askExamples || []).map((question) => (
                    <li key={question}>{question}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="birth-section birth-compare">
              <h2>{page.compareTitle}</h2>
              <p>{page.compareText}</p>
              <Link className="birth-draw" to={chartHref('/birth-chart-compatibility/', language)}>{page.compareCta}</Link>
            </div>
            <div className="birth-faq">
              {page.faq.map((item) => (
                <article key={item.q}>
                  <h3>{item.q}</h3>
                  <p>{item.a}</p>
                </article>
              ))}
            </div>
            <nav className="birth-box birth-more" aria-label={page.moreLabel}>
              <h2>{page.moreTitle}</h2>
              <ul>
                {MORE.filter((item) => item.key !== 'chart').map((item) => (
                  <li key={item.to}>
                    <Link to={item.key === 'synastry' ? chartHref(item.to, language) : item.to}>
                      {page.more[item.key] || item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

const BirthChart = () => (
  <ChartLanguageProvider>
    <BirthChartPage />
  </ChartLanguageProvider>
);

export default BirthChart;
